import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MessageCircle, Gauge, Zap, TrendingUp } from 'lucide-react';
import CalculatorWidget from '../components/CalculatorWidget';
import { useSeo } from '../hooks/useSeo';
import { seo, LOGO_URL } from '../seoConfig';
import LegalLinks from '../components/LegalLinks';

const PowerCalculator = () => {
  useSeo(seo.calculator);

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black/95 backdrop-blur-md z-50 border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center">
              <img src={LOGO_URL} alt="Powerlab Works" className="h-14 w-auto object-contain" />
            </a>
            <nav className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-300 hover:text-[#FF6600] transition-colors">Home</a>
              <a href="#calculator" className="text-[#FF6600] font-semibold">Power Calculator</a>
              <a href="/faq" className="text-gray-300 hover:text-[#FF6600] transition-colors">FAQ</a>
              <a href="/#contact" className="text-gray-300 hover:text-[#FF6600] transition-colors">Contact</a>
            </nav>
            <Button
              onClick={() => window.location.href = 'mailto:info@powerlab.works?subject=Appointment Request&body=Hello, I would like to book an appointment for chip tuning.'}
              className="bg-[#FF6600] hover:bg-[#E55A00] text-white"
            >
              Book Appointment
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              Power <span className="text-[#FF6600]">Calculator</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Discover your vehicle&apos;s true potential with real tuning data from our database of 14,210 engines
            </p>
          </div>

          <CalculatorWidget />
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Why Choose Powerlab Works?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-[#1a1a1a] border-gray-800">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-[#FF6600]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Gauge className="w-6 h-6 text-[#FF6600]" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Real Database</h3>
                  <p className="text-gray-400 text-sm">14,210 engines with verified performance data</p>
                </CardContent>
              </Card>
              <Card className="bg-[#1a1a1a] border-gray-800">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-[#FF6600]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Zap className="w-6 h-6 text-[#FF6600]" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Safe & Reliable</h3>
                  <p className="text-gray-400 text-sm">All tuning within safe margins with dyno testing</p>
                </CardContent>
              </Card>
              <Card className="bg-[#1a1a1a] border-gray-800">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-[#FF6600]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-6 h-6 text-[#FF6600]" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">14+ Years Experience</h3>
                  <p className="text-gray-400 text-sm">Active since 2012, 300,000+ vehicle files tuned worldwide</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-900 py-10">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600 text-xs">© 2025 Powerlab Works OU. All rights reserved.</p>
          <LegalLinks lang="en" className="mt-3" />
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a
        href="https://wa.me/905327866635?text=Hello, I would like to know more about Powerlab tuning services"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 md:top-1/2 md:bottom-auto md:-translate-y-1/2 bg-[#25D366] hover:bg-[#1DA851] text-white p-4 rounded-full shadow-2xl z-40 transition-all hover:scale-110 flex items-center justify-center"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </a>
    </div>
  );
};

export default PowerCalculator;
